import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import * as api from "../api";

// ─── Composant modal de réponse ──────────────────────────────
function ModalReponse({ rfq, onClose, onSuccess }) {
  const [form, setForm] = useState({ message: "", prix: "", delai: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { setError("Le message est requis."); return; }
    setLoading(true);
    try {
      // Composer le contenu de la réponse avec tous les champs
      let contenu = form.message;
      if (form.prix) contenu += `\n\n💰 Prix proposé : ${form.prix} DA`;
      if (form.delai) contenu += `\n⏱️ Délai de livraison : ${form.delai}`;

      await api.repondreRFQ({ rfqId: rfq.id, reponse: contenu });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 14px", border: "1px solid #d1d5db",
    borderRadius: "8px", fontSize: "14px", outline: "none", boxSizing: "border-box"
  };

  return (
    <div style={{
      position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px"
    }}>
      <div style={{
        backgroundColor: "white", borderRadius: "16px", padding: "32px",
        maxWidth: "560px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "24px" }}>
          <div>
            <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1f2937", margin: 0 }}>
              Répondre à la demande
            </h2>
            <p style={{ color: "#6b7280", fontSize: "14px", marginTop: "4px" }}>
              De {rfq.nom} — {rfq.type_produit || "Demande générale"}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#6b7280", fontSize: "20px", padding: "4px", lineHeight: 1
          }}>✕</button>
        </div>

        {/* Rappel besoin */}
        {rfq.besoin && (
          <div style={{
            backgroundColor: "#f0fdfa", border: "1px solid #99f6e4",
            borderRadius: "8px", padding: "12px 16px", marginBottom: "20px"
          }}>
            <p style={{ color: "#134e4a", fontSize: "13px", margin: 0 }}>
              <strong>Besoin :</strong> {rfq.besoin}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#374151", marginBottom: "6px" }}>
              Votre offre / message *
            </label>
            <textarea
              value={form.message}
              onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
              placeholder="Décrivez votre offre, vos disponibilités, vos références..."
              rows="5"
              required
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#374151", marginBottom: "6px" }}>
                Prix proposé (DA)
              </label>
              <input
                type="text"
                value={form.prix}
                onChange={e => setForm(p => ({ ...p, prix: e.target.value }))}
                placeholder="Ex: 1 200 000"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "#374151", marginBottom: "6px" }}>
                Délai de livraison
              </label>
              <input
                type="text"
                value={form.delai}
                onChange={e => setForm(p => ({ ...p, delai: e.target.value }))}
                placeholder="Ex: 3 mois"
                style={inputStyle}
              />
            </div>
          </div>
          {error && (
            <p style={{ color: "#dc2626", fontSize: "14px", marginBottom: "12px" }}>{error}</p>
          )}
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{
              padding: "10px 20px", backgroundColor: "white", color: "#374151",
              border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px",
              fontWeight: "500", cursor: "pointer"
            }}>Annuler</button>
            <button type="submit" disabled={loading} style={{
              padding: "10px 24px", backgroundColor: loading ? "#94d9d5" : "#0d9488",
              color: "white", border: "none", borderRadius: "8px", fontSize: "14px",
              fontWeight: "600", cursor: loading ? "not-allowed" : "pointer"
            }}>
              {loading ? "Envoi..." : "Envoyer l'offre"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Composant modal des réponses (pour acheteur / admin) ────
function ModalReponses({ rfq, onClose }) {
  const [reponses, setReponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getReponsesRFQ(rfq.id)
      .then(r => setReponses(r.data))
      .catch(() => setReponses([]))
      .finally(() => setLoading(false));
  }, [rfq.id]);

  return (
    <div style={{
      position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "20px"
    }}>
      <div style={{
        backgroundColor: "white", borderRadius: "16px", padding: "32px",
        maxWidth: "640px", width: "100%", maxHeight: "80vh", overflow: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "24px" }}>
          <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1f2937", margin: 0 }}>
            Offres reçues ({reponses.length})
          </h2>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#6b7280", fontSize: "20px", padding: "4px", lineHeight: 1
          }}>✕</button>
        </div>

        {loading ? (
          <p style={{ color: "#6b7280", textAlign: "center" }}>Chargement...</p>
        ) : reponses.length === 0 ? (
          <p style={{ color: "#6b7280", textAlign: "center" }}>Aucune offre reçue pour l'instant.</p>
        ) : (
          reponses.map(rep => (
            <div key={rep.id} style={{
              border: "1px solid #e5e7eb", borderRadius: "12px", padding: "20px",
              marginBottom: "16px", backgroundColor: "#fafafa"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                {rep.avatar ? (
                  <img src={rep.avatar} alt="" style={{ width: "42px", height: "42px", borderRadius: "50%", objectFit: "cover" }} />
                ) : (
                  <div style={{
                    width: "42px", height: "42px", borderRadius: "50%",
                    backgroundColor: "#0d9488", display: "flex", alignItems: "center", justifyContent: "center",
                    color: "white", fontWeight: "700", fontSize: "16px"
                  }}>
                    {rep.prenom?.[0]}{rep.nom?.[0]}
                  </div>
                )}
                <div>
                  <p style={{ margin: 0, fontWeight: "600", color: "#1f2937" }}>{rep.prenom} {rep.nom}</p>
                  <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>{rep.entreprise || rep.email}</p>
                </div>
                <span style={{ marginLeft: "auto", fontSize: "12px", color: "#9ca3af" }}>
                  {new Date(rep.created_at).toLocaleDateString("fr-FR")}
                </span>
              </div>
              <div style={{ backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "14px", marginBottom: "10px" }}>
                <p style={{ margin: 0, color: "#374151", lineHeight: "1.6" }}>{rep.message}</p>
              </div>
              {(rep.prix || rep.delai) && (
                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                  {rep.prix && (
                    <span style={{
                      backgroundColor: "#f0fdfa", color: "#0d9488", padding: "4px 12px",
                      borderRadius: "20px", fontSize: "13px", fontWeight: "600"
                    }}>💰 {rep.prix} DA</span>
                  )}
                  {rep.delai && (
                    <span style={{
                      backgroundColor: "#eff6ff", color: "#2563eb", padding: "4px 12px",
                      borderRadius: "20px", fontSize: "13px", fontWeight: "600"
                    }}>⏱️ {rep.delai}</span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Page principale ─────────────────────────────────────────
function DemandRFQ() {
  const { user } = useAuth();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalReponse, setModalReponse] = useState(null);   // rfq en cours de réponse
  const [modalOffres, setModalOffres] = useState(null);     // rfq dont on voit les offres
  const [toast, setToast] = useState("");

  const charger = async () => {
    setLoading(true);
    try {
      const res = await api.getAllRFQ();
      setRfqs(res.data);
    } catch {
      setRfqs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const handleReponseSuccess = () => {
    setModalReponse(null);
    charger();
    showToast("✅ Votre offre a été envoyée à l'acheteur !");
  };

  const badgeStatut = (statut) => {
    const map = {
      en_attente: { label: "En attente", bg: "#fef3c7", color: "#92400e" },
      traite:     { label: "Traité",     bg: "#d1fae5", color: "#065f46" },
      refuse:     { label: "Refusé",     bg: "#fee2e2", color: "#991b1b" },
    };
    const s = map[statut] || map.en_attente;
    return (
      <span style={{ backgroundColor: s.bg, color: s.color, padding: "3px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" }}>
        {s.label}
      </span>
    );
  };

  if (!user || !["admin", "exposant"].includes(user.role)) {
    return (
      <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        <Navbar />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <p style={{ color: "#6b7280" }}>Accès réservé aux exposants et administrateurs.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f9fafb" }}>
      <Navbar />

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: "20px", right: "20px", zIndex: 2000,
          backgroundColor: "#0d9488", color: "white", padding: "14px 20px",
          borderRadius: "10px", fontWeight: "600", boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
        }}>{toast}</div>
      )}

      <div style={{ flex: 1, padding: "40px 20px", maxWidth: "1100px", margin: "0 auto", width: "100%" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#1f2937", margin: 0 }}>
              📋 Demandes de devis reçues
            </h1>
            <p style={{ color: "#6b7280", marginTop: "6px" }}>
              {user.role === "admin"
                ? "Toutes les demandes de la plateforme"
                : "Demandes générales + demandes sur vos annonces"}
            </p>
          </div>
          <span style={{
            backgroundColor: "#0d9488", color: "white", padding: "6px 18px",
            borderRadius: "20px", fontSize: "14px", fontWeight: "600"
          }}>{rfqs.length} demande{rfqs.length !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "80px", color: "#6b7280" }}>Chargement...</div>
        ) : rfqs.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "80px", backgroundColor: "white",
            borderRadius: "16px", border: "1px solid #e5e7eb"
          }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📭</div>
            <h3 style={{ color: "#1f2937", marginBottom: "8px" }}>Aucune demande pour l'instant</h3>
            <p style={{ color: "#6b7280" }}>Les demandes de devis des acheteurs apparaîtront ici.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {rfqs.map(rfq => (
              <div key={rfq.id} style={{
                backgroundColor: "white", borderRadius: "14px", padding: "24px",
                border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                borderLeft: rfq.deja_repondu ? "4px solid #0d9488" : "4px solid #f59e0b"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", flexWrap: "wrap", gap: "12px" }}>
                  <div style={{ flex: 1 }}>
                    {/* Ligne titre */}
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
                      <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "700", color: "#1f2937" }}>
                        {rfq.type_produit ? rfq.type_produit.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) : "Demande de devis"}
                      </h3>
                      {badgeStatut(rfq.statut)}
                      {rfq.annonce_nom && (
                        <span style={{ backgroundColor: "#eff6ff", color: "#2563eb", padding: "3px 10px", borderRadius: "20px", fontSize: "12px" }}>
                          📦 {rfq.annonce_nom}
                        </span>
                      )}
                      {!rfq.annonce_id && (
                        <span style={{ backgroundColor: "#fef3c7", color: "#92400e", padding: "3px 10px", borderRadius: "20px", fontSize: "12px" }}>
                          🌐 Demande générale
                        </span>
                      )}
                      {rfq.deja_repondu && (
                        <span style={{ backgroundColor: "#d1fae5", color: "#065f46", padding: "3px 10px", borderRadius: "20px", fontSize: "12px" }}>
                          ✅ Vous avez répondu
                        </span>
                      )}
                    </div>

                    {/* Infos acheteur */}
                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "12px" }}>
                      <span style={{ color: "#374151", fontSize: "14px" }}>
                        <strong>Acheteur :</strong> {rfq.nom}
                      </span>
                      <span style={{ color: "#6b7280", fontSize: "14px" }}>
                        📧 {rfq.email}
                      </span>
                      {rfq.telephone && (
                        <span style={{ color: "#6b7280", fontSize: "14px" }}>
                          📞 {rfq.telephone}
                        </span>
                      )}
                    </div>

                    {/* Besoin */}
                    {rfq.besoin && (
                      <p style={{
                        backgroundColor: "#f9fafb", border: "1px solid #e5e7eb",
                        borderRadius: "8px", padding: "10px 14px", color: "#4b5563",
                        fontSize: "14px", margin: "0 0 12px", lineHeight: "1.5"
                      }}>
                        {rfq.besoin}
                      </p>
                    )}

                    {/* Meta */}
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                      {rfq.quantite > 1 && (
                        <span style={{ color: "#6b7280", fontSize: "13px" }}>Qté: {rfq.quantite}</span>
                      )}
                      {rfq.date_limite && (
                        <span style={{ color: "#6b7280", fontSize: "13px" }}>
                          Avant le {new Date(rfq.date_limite).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                      <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                        Reçu le {new Date(rfq.created_at).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: "160px" }}>
                    {user.role === "exposant" && (
                      <button
                        onClick={() => setModalReponse(rfq)}
                        style={{
                          padding: "10px 18px", backgroundColor: rfq.deja_repondu ? "#f3f4f6" : "#0d9488",
                          color: rfq.deja_repondu ? "#374151" : "white",
                          border: "none", borderRadius: "8px", fontSize: "14px",
                          fontWeight: "600", cursor: "pointer", textAlign: "center"
                        }}
                      >
                        {rfq.deja_repondu ? "✏️ Modifier l'offre" : "📨 Répondre"}
                      </button>
                    )}
                    <button
                      onClick={() => setModalOffres(rfq)}
                      style={{
                        padding: "10px 18px", backgroundColor: "white", color: "#374151",
                        border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px",
                        fontWeight: "500", cursor: "pointer", textAlign: "center"
                      }}
                    >
                      💼 Offres ({rfq.nb_reponses || 0})
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {modalReponse && (
        <ModalReponse
          rfq={modalReponse}
          onClose={() => setModalReponse(null)}
          onSuccess={handleReponseSuccess}
        />
      )}
      {modalOffres && (
        <ModalReponses
          rfq={modalOffres}
          onClose={() => setModalOffres(null)}
        />
      )}

      <Footer />
    </div>
  );
}

export default DemandRFQ;